import React, { useEffect, useRef, useState } from 'react';
import { authFetch } from '../../services/auth';
import { Truck, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 20000;
const FADE_AFTER_MS = 30000;

/**
 * Floating bottom-right "Auto-approved" badge that briefly pulses each time
 * the guard lets a delivery in for this resident's villa. Polls every 20s.
 */
export const DeliveryArrivalToast = () => {
  const [active, setActive] = useState(null); // { id, provider, purpose, entry_time }
  const sinceRef = useRef(new Date().toISOString());
  const seenIdsRef = useRef(new Set());
  const fadeTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const res = await authFetch(
          `${API}/api/resident/recent-delivery-arrivals?since=${encodeURIComponent(sinceRef.current)}`
        );
        if (!res.ok) return;
        const json = await res.json();
        if (cancelled) return;
        sinceRef.current = json.now || sinceRef.current;
        const fresh = (json.arrivals || []).filter((a) => !seenIdsRef.current.has(a.id));
        if (fresh.length > 0) {
          // Mark all as seen
          fresh.forEach((a) => seenIdsRef.current.add(a.id));
          // Show the most recent one (already sorted desc)
          setActive(fresh[0]);
          if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
          fadeTimerRef.current = setTimeout(() => setActive(null), FADE_AFTER_MS);
        }
      } catch {
        // Silent — don't disrupt the dashboard if the network blips.
      }
    };

    poll();
    const handle = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(handle);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
    };
  }, []);

  if (!active) return null;

  return (
    <div
      className="fixed bottom-5 right-5 z-50 max-w-sm animate-in slide-in-from-right-5 fade-in duration-500"
      data-testid="delivery-arrival-toast"
    >
      <div className="rounded-2xl bg-gradient-to-br from-[#14D4C6] to-[#0EA5A0] text-slate-900 shadow-2xl p-4 pr-5 flex items-start gap-3 border border-white/30">
        <div className="relative shrink-0">
          <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
          <div className="relative w-10 h-10 rounded-full bg-white/90 flex items-center justify-center">
            <Truck className="w-5 h-5 text-[#0EA5A0]" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-900/80">
            <CheckCircle2 className="w-3 h-3" />
            Auto-approved at the gate
          </div>
          <div
            className="mt-0.5 text-sm font-bold text-slate-900 truncate"
            data-testid="delivery-arrival-provider"
          >
            {active.provider || 'Delivery'}{' '}
            {active.purpose && (
              <span className="font-medium text-slate-900/80">
                · {active.purpose.replace(/^Delivery\s*[—-]?\s*/i, '').trim() || 'arrived'}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-slate-900/60">
            {new Date(active.entry_time).toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
            {' · '}
            <button
              onClick={() => setActive(null)}
              className="underline-offset-2 hover:underline font-medium"
              data-testid="delivery-arrival-dismiss"
            >
              dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
